/**
 * MoneySnapshot widget -- Quick view of group balances from BalanceTab.
 */

import type { Widget, WidgetContext } from '../types';

interface BalanceEntry {
  group: string;
  type: 'owe' | 'owed';
  person: string;
  amount: number;
  currency: string;
}

function getBalances(): BalanceEntry[] {
  try {
    const raw = localStorage.getItem('aweni_money_snapshot');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* */ }
  // No real data -- return empty (no mock data that shows raw placeholders)
  return [];
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export const MoneySnapshot: Widget = {
  id: 'money-snapshot',
  label: { en: 'Money Snapshot', fr: 'Apercu financier', ar: 'نظرة مالية', es: 'Resumen financiero', de: 'Finanzuebersicht' },
  icon: '💰',
  defaultVisible: true,
  defaultOrder: 9,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const balances = getBalances();

    if (balances.length === 0) {
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="balancetab">
          <div class="flex items-center gap-2">
            <span>\u{1F4B0}</span>
            <span class="text-sm" style="color:var(--text-secondary);">${ctx.t('widget_money_no_data')}</span>
          </div>
        </div>`;
      container.querySelector('[data-nav]')?.addEventListener('click', () => ctx.navigate('balancetab'));
      return;
    }

    // Calculate net
    const net = balances.reduce((sum, b) => sum + (b.type === 'owed' ? b.amount : -b.amount), 0);

    if (size === 'compact') {
      const first = balances[0];
      const text = first.type === 'owe'
        ? ctx.t('widget_money_you_owe', { amount: formatCurrency(first.amount, first.currency), person: first.person })
        : ctx.t('widget_money_owes_you', { person: first.person, amount: formatCurrency(first.amount, first.currency) });

      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer flex items-center justify-between" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="balancetab">
          <div class="flex items-center gap-2">
            <span>💰</span>
            <span class="text-sm" style="color:var(--text-primary);">${text}</span>
          </div>
          <button class="text-xs px-3 py-1 rounded-full" style="background:#8B5CF620;color:#8B5CF6;">${ctx.t('balancetab_settle_up')}</button>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold uppercase tracking-wide" style="color:var(--text-secondary);">💰 ${ctx.t('widget_money_title')}</h3>
            <span class="text-sm font-bold" style="color:${net >= 0 ? '#10B981' : '#EF4444'};">
              ${net >= 0 ? '+' : ''}${formatCurrency(net, balances[0].currency)}
            </span>
          </div>
          <div class="space-y-2">
            ${balances.map((b) => {
              const text = b.type === 'owe'
                ? ctx.t('widget_money_you_owe', { amount: formatCurrency(b.amount, b.currency), person: b.person })
                : ctx.t('widget_money_owes_you', { person: b.person, amount: formatCurrency(b.amount, b.currency) });
              return `
                <div class="flex items-center justify-between p-2 rounded-xl" style="background:var(--bg-secondary);">
                  <div>
                    <p class="text-xs" style="color:var(--text-secondary);">${b.group}</p>
                    <p class="text-sm" style="color:var(--text-primary);">${text}</p>
                  </div>
                </div>`;
            }).join('')}
          </div>
          <button class="mt-3 w-full text-sm font-medium py-2 rounded-xl transition-transform active:scale-95" style="background:#8B5CF620;color:#8B5CF6;" data-nav="balancetab">
            ${ctx.t('balancetab_settle_up')}
          </button>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('balancetab'));
    });
  },
};
